import { TenantLoginView } from '@today/ui-domain'
import { BrandProvider } from '@today/ui-primitives'
import { useTranslation } from 'react-i18next'
import { useParams } from 'react-router-dom'
import { useAuthLogo } from '@/auth/useAuthLogo'
import { useLoginBrand } from '@/brand/use-login-brand'
import { useTenantLoginOptions } from '@/hooks/useTenantLoginOptions'
import { AUTH_NAMESPACE } from '@/i18n'

/**
 * `/t/:slug/login` — the per-tenant login surface (TO-410 REQ-008; TO-361
 * login-surface REQ-002). Redirects straight to the only sign-in option, or
 * offers the tenant's options in order. Binds the tenant's brand (TO-541 REQ-104)
 * through a nested provider, which restores the app binding on unmount.
 */
export function TenantLoginPage() {
	const { slug = '' } = useParams()
	const login = useTenantLoginOptions(slug)
	const brand = useLoginBrand(login.brandId)
	return (
		<BrandProvider brand={brand} applyToDocument>
			<TenantLoginContent slug={slug} login={login} />
		</BrandProvider>
	)
}

/** The login surface itself, inside the tenant's brand (its logo is that brand's). */
function TenantLoginContent({ slug, login }: { slug: string; login: ReturnType<typeof useTenantLoginOptions> }) {
	const { options, isLoading, select } = login
	const { t } = useTranslation(AUTH_NAMESPACE)
	const logo = useAuthLogo()

	return (
		<TenantLoginView
			tenantSlug={slug}
			options={options}
			isLoading={isLoading}
			onSelect={select}
			logo={logo}
			labels={{
				title: (value) => t('tenant.title', { slug: value }),
				subtitle: t('login.redirectHint'),
				checking: (value) => t('tenant.checking', { slug: value }),
				native: t('tenant.native'),
				sso: (name) => t('tenant.sso', { name }),
				ssoUnnamed: t('tenant.ssoUnnamed'),
			}}
		/>
	)
}
